import React, { useEffect, useState } from "react";
import closeSvg from "../../assets/images/uncheck.svg";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import { formatSpentValue, langNames } from "../../utils/constants";

const SOURCES = [
  { key: "dfp", label: "DFP" },
  { key: "gam", label: "GAM" },
];

// Position wise error report (including No Fill) for DFP and GAM.
// Rows are positions, columns are error codes; clicking a count opens the
// ad code wise breakdown of that code at that position.
function ErrorsReport({ dateFilter, selectedLanguage, selectedDistrict }) {
  const [loader, setLoader] = useState(false);
  const [report, setReport] = useState({ dfp: [], gam: [] });
  const [source, setSource] = useState("dfp");
  const [adCodeView, setAdCodeView] = useState(null);

  const filters = { date: dateFilter, lang_id: selectedLanguage, district_id: selectedDistrict };

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoader(true);
      setAdCodeView(null);
      try {
        const res = await api({
          url: "/inventory/get-errors-report",
          method: "POST",
          responseType: "json",
          data: filters,
        });
        if (cancelled) return;
        if (res.code === 200) {
          setReport({ dfp: res.data?.dfp || [], gam: res.data?.gam || [] });
        } else {
          Toast("error", res.data?.message || res.message || "Failed to fetch errors report");
          setReport({ dfp: [], gam: [] });
        }
      } catch (error) {
        console.error("Error @ fetchErrorsReport", error);
        if (!cancelled) setReport({ dfp: [], gam: [] });
      }
      if (!cancelled) setLoader(false);
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [dateFilter, selectedLanguage, selectedDistrict]);

  const fetchAdCodes = async (position, description) => {
    try {
      const res = await api({
        url: "/inventory/get-errors-report-adcodes",
        method: "POST",
        responseType: "json",
        data: { ...filters, position, description, source },
      });
      if (res.code === 200) {
        setAdCodeView({ source, position, description, data: res.data || [] });
      } else {
        Toast("error", res.data?.message || res.message || "Failed to fetch ad code wise errors");
      }
    } catch (error) {
      console.error("Error @ fetchAdCodes", error);
    }
  };

  // Pivot rows ({position, description, count}) into position x error code.
  const rows = report[source] || [];
  const codeTotals = {};
  const positionMap = {};
  for (const r of rows) {
    const count = +r.count || 0;
    codeTotals[r.description] = (codeTotals[r.description] || 0) + count;
    positionMap[r.position] = positionMap[r.position] || { position: +r.position, codes: {}, total: 0 };
    positionMap[r.position].codes[r.description] = count;
    positionMap[r.position].total += count;
  }
  const codes = Object.keys(codeTotals).sort((a, b) => codeTotals[b] - codeTotals[a]);
  const positions = Object.values(positionMap)
    .filter((p) => p.position !== 0)
    .sort((a, b) => a.position - b.position);
  const grandTotal = positions.reduce((acc, p) => acc + p.total, 0);

  return (
    <div className="errors-report">
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0 12px" }}>
        <div className="inv2-seg">
          {SOURCES.map((s) => (
            <button key={s.key} className={source === s.key ? "active" : ""} onClick={() => setSource(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <span className="mono" style={{ fontSize: 13 }}>
          Total errors: <b title={grandTotal}>{formatSpentValue(grandTotal)}</b>
        </span>
      </div>

      {loader ? (
        <div style={{ padding: "60px 0", textAlign: "center", color: "oklch(0.55 0.02 260)" }}>Loading…</div>
      ) : !positions.length ? (
        <div style={{ padding: "60px 0", textAlign: "center", color: "oklch(0.55 0.02 260)" }}>No Errors Found</div>
      ) : (
        <div style={{ overflow: "auto", maxHeight: "calc(100vh - 170px)" }}>
          <table className="table table-bordered table-impress">
            <thead>
              <tr>
                <th>Position</th>
                {codes.map((code) => (
                  <th key={code} title={code || "(empty)"} style={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {code || "(empty)"}
                  </th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.position}>
                  <td className="mono">{p.position}</td>
                  {codes.map((code) => {
                    const count = p.codes[code] || 0;
                    return (
                      <td
                        key={code}
                        className={`mono ${count ? "cursor-pointer text-blue-600" : ""}`}
                        title={count ? `${count} — click for ad code wise` : ""}
                        onClick={() => count && fetchAdCodes(p.position, code)}
                      >
                        {count ? formatSpentValue(count) : "·"}
                      </td>
                    );
                  })}
                  <td className="mono" title={p.total}><b>{formatSpentValue(p.total)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adCodeView ? (
        <div className="inventory-ad-view" onClick={() => setAdCodeView(null)}>
          <div className="popupview" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setAdCodeView(null)}>
              <img src={closeSvg} alt="close" />
            </button>
            <div className="d-flex justify-between gap-2">
              <h5>Date: <span>{dateFilter}</span></h5>
              <h5>Language: <span className="capitalize">{langNames[selectedLanguage] || "All"}</span></h5>
              <h5>Source: <span>{adCodeView.source.toUpperCase()}</span></h5>
              <h5>Position: <span>{adCodeView.position}</span></h5>
              <h5>Count: <span>{formatSpentValue(adCodeView.data.reduce((acc, i) => acc + +i.count, 0))}</span></h5>
            </div>
            <h5>Error: <span>{adCodeView.description || "(empty)"}</span></h5>
            <div className="over-flow-view">
              <table className="table table-bordered table-impress">
                <thead>
                  <tr>
                    <th>Ad Code</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {adCodeView.data.map((item, index) => (
                    <tr key={index}>
                      <td>{item.ad_code}</td>
                      <td>{formatSpentValue(item.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ErrorsReport;
